/**
 * usePreference — single hook for every persistable UI setting.
 *
 * The backend returns BOTH default + user JSON blobs for a (scope, key) pair.
 * This hook deep-merges them so callers can `value.something` directly, and
 * exposes:
 *
 *   - `value`        : effective state (default ⊕ user); read this for rendering
 *   - `defaults`     : raw baseline (useful for "you've customised" badges)
 *   - `userOverride` : the user delta only (what's stored in the user row)
 *   - `patch(partial)`: deep-merged PATCH into the user row (auto-debounced
 *                       outside this hook by the caller if needed)
 *   - `reset(subKey?)`: drop the whole user row OR one sub-key — UI reverts to
 *                       default. Wire to "Default columns" / "Default filter".
 *   - `isCustomised(subKey?)`: true iff there's any user delta to reset
 *
 * Two flavours:
 *
 *   usePreference<T>(scope, key)
 *     → `value` typed T; merge happens at top level. Use when shape is a single
 *       flat object (e.g. page_filter).
 *
 *   usePreference<T>(scope, key, subKey)
 *     → `value` typed T = the merged sub-tree at `userDoc[subKey] ⊕ default[subKey]`.
 *       Use when scope shape is `{<subKey>: T}` (e.g. columns by mode).
 */
import { useCallback, useMemo } from 'react'

import {
  useGetPreferenceQuery,
  usePatchPreferenceMutation,
  useResetPreferenceMutation,
} from '@/store/api/userPreferencesApi'
import { useEnsureDefaultEffect } from '@/hooks/useEnsureDefaultEffect'

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function deepMerge<T>(base: T, override: Partial<T>): T {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return (override ?? base) as T
  }
  const out: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(override)) {
    const baseV = out[k]
    if (isPlainObject(v) && isPlainObject(baseV)) {
      out[k] = deepMerge(baseV, v as Record<string, unknown>)
    } else if (v !== undefined) {
      out[k] = v
    }
  }
  return out as T
}

export interface UsePreferenceResult<T> {
  value: T
  defaults: T
  userOverride: Partial<T>
  isLoading: boolean
  isFetching: boolean
  isCustomised: boolean
  /** Deep-merge `partial` into the user row. */
  patch: (partial: Partial<T>) => Promise<unknown>
  /** Drop user delta — UI reverts to default. `subKey` ignored unless this
   *  hook was constructed with one. */
  reset: () => Promise<unknown>
}

export interface UsePreferenceOptions {
  /** true → GET refetch mỗi lần component mount (đọc lại DB tươi mỗi lần vào page).
   *  Dùng cho `navigation`: chuyển Navigation phải query lại DB để load đúng tab
   *  cuối, không đọc cache cũ (tránh "lúc được lúc không"). Mặc định false. */
  refetchOnMount?: boolean
}

export function usePreference<T extends Record<string, any>>(
  scope: string,
  prefKey: string,
): UsePreferenceResult<T>
export function usePreference<T extends Record<string, any>>(
  scope: string,
  prefKey: string,
  subKey: string | null | undefined,
): UsePreferenceResult<T>
export function usePreference<T extends Record<string, any>>(
  scope: string,
  prefKey: string,
  subKey: string | null | undefined,
  options: UsePreferenceOptions,
): UsePreferenceResult<T>
export function usePreference<T extends Record<string, any>>(
  scope: string,
  prefKey: string,
  subKey?: string | null,
  options?: UsePreferenceOptions,
): UsePreferenceResult<T> {
  // Auto-bootstrap default row on first visit (idempotent per session).
  useEnsureDefaultEffect(scope, prefKey, subKey ?? undefined)

  // Mặc định `refetchOnMountOrArgChange: false` (preference cột/filter là config
  // singleton, mutation đã cập nhật cache → khỏi refetch thừa mỗi mount).
  // `navigation` truyền refetchOnMount:true → mỗi lần chuyển navigation query lại DB.
  const { data, isLoading, isFetching } = useGetPreferenceQuery(
    { scope, prefKey },
    { refetchOnMountOrArgChange: options?.refetchOnMount ?? false },
  )
  const [patchPref] = usePatchPreferenceMutation()
  const [resetPref] = useResetPreferenceMutation()

  const empty: Record<string, unknown> = useMemo(() => ({}), [])

  const defaultsDoc = (data?.default ?? empty) as Record<string, unknown>
  const userDoc = (data?.user ?? empty) as Record<string, unknown>

  const defaults = useMemo<T>(() => {
    if (subKey === undefined || subKey === null) return defaultsDoc as T
    return ((defaultsDoc[subKey] as T) ?? (empty as T))
  }, [defaultsDoc, empty, subKey])

  const userOverride = useMemo<Partial<T>>(() => {
    if (subKey === undefined || subKey === null) return userDoc as Partial<T>
    return ((userDoc[subKey] as Partial<T>) ?? (empty as Partial<T>))
  }, [userDoc, empty, subKey])

  const value = useMemo<T>(
    () => deepMerge<T>(defaults, userOverride),
    [defaults, userOverride],
  )

  const isCustomised = useMemo(() => {
    if (subKey === undefined || subKey === null) {
      return Object.keys(userDoc).length > 0
    }
    return userDoc[subKey] !== undefined
  }, [subKey, userDoc])

  const patch = useCallback(
    (partial: Partial<T>) => {
      const body =
        subKey === undefined || subKey === null
          ? (partial as Record<string, unknown>)
          : { [subKey]: partial as Record<string, unknown> }
      return patchPref({ scope, prefKey, patch: body }).unwrap()
    },
    [patchPref, scope, prefKey, subKey],
  )

  const reset = useCallback(
    () =>
      resetPref({
        scope,
        prefKey,
        subKey: subKey === null ? undefined : subKey,
      }).unwrap(),
    [resetPref, scope, prefKey, subKey],
  )

  return {
    value,
    defaults,
    userOverride,
    isLoading,
    isFetching,
    isCustomised,
    patch,
    reset,
  }
}
